import React, { useRef, useLayoutEffect, useState } from 'react';
import { Tooltip } from '../Tooltip';
import { Copy } from '../Copy';

type TextFieldProps = {
  text: string | null;
  fallback?: string;
  href?: string | null;
  showCopy?: boolean;
  minTextLengthForTooltip?: number;
  tooltipContent?: string;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  textClassName?: string;
  copyClassName?: string;
  copyIconClassName?: string;
  formatTextForDisplay?: (text: string) => string;
  onClick?: (e: React.MouseEvent) => void;
};

export const TextField: React.FC<TextFieldProps> = ({
  text,
  fallback = '-',
  href,
  showCopy = true,
  tooltipContent,
  tooltipPosition = 'top',
  className,
  textClassName,
  copyClassName,
  copyIconClassName,
  formatTextForDisplay,
  onClick,
}) => {
  const wrapperRef = useRef<HTMLAnchorElement | HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    if (!text) return;

    const checkTruncation = () => {
      if (wrapperRef.current) {
        setIsTruncated(
          wrapperRef.current.scrollWidth > wrapperRef.current.clientWidth
        );
      }
    };
    checkTruncation();
  }, [text, href]);

  if (!text) {
    return <span className="text-secondary">{fallback}</span>;
  }

  const displayText =
    href && formatTextForDisplay ? formatTextForDisplay(text || '') : text;
  const tooltipText = tooltipContent || text;

  const textContent = (
    <span
      className={`text-mainText ${href ? 'hover:text-main underline' : ''} ${textClassName || ''}`}
    >
      {displayText}
    </span>
  );

  const wrappedText = isTruncated ? (
    <Tooltip content={tooltipText} position={tooltipPosition} trigger="hover">
      {href ? (
        <a
          ref={wrapperRef as React.RefObject<HTMLAnchorElement>}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="block truncate"
          onClick={e => {
            onClick?.(e);
            e.stopPropagation();
          }}
        >
          {textContent}
        </a>
      ) : (
        <div
          ref={wrapperRef as React.RefObject<HTMLDivElement>}
          className="truncate"
          onClick={onClick}
        >
          {textContent}
        </div>
      )}
    </Tooltip>
  ) : href ? (
    <a
      ref={wrapperRef as React.RefObject<HTMLAnchorElement>}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="block truncate"
      onClick={e => {
        onClick?.(e);
        e.stopPropagation();
      }}
    >
      {textContent}
    </a>
  ) : (
    <div
      ref={wrapperRef as React.RefObject<HTMLDivElement>}
      className="truncate"
      onClick={onClick}
    >
      {textContent}
    </div>
  );

  return (
    <div
      className={`flex items-center gap-2 min-w-0 w-full ${className || ''}`}
    >
      <div className="flex-1 min-w-0 overflow-hidden">{wrappedText}</div>
      {showCopy && (
        <Copy
          text={text}
          className={`shrink-0 flex items-center ${copyClassName || ''}`}
          iconClassName={`text-secondary hover:text-main transition-colors ${copyIconClassName || ''}`}
        />
      )}
    </div>
  );
};
